<template>
  <section class="section dashboard" v-if="plan">
    <div class="row">

      <!-- Header -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="filter">
              <ActionDropdown>
                <li class="dropdown-header text-start">
                  <h6>Действия</h6>
                </li>

                <li>
                  <a
                      @click.prevent="emit('update', plan)"
                      class="dropdown-item"
                      href="#"
                  >Изменить</a>
                </li>
                <li>
                  <a
                      @click.prevent="emit('delete', plan)"
                      class="dropdown-item"
                      href="#"
                  >Удалить</a>
                </li>
              </ActionDropdown>
            </div>
            <h5 class="card-title">
              {{ planCard.name }}
              <span class="ms-2 badge bg-warning text-bg-light" v-if="planCard.type === 'EXPENSE'">Расход</span>
              <span class="ms-2 badge bg-success text-bg-light" v-else>Доход</span>
              <span class="ms-2 badge bg-blue text-bg-light">{{ planCard.scopeTypeLabel }}</span>
            </h5>

            <div class="row">
              <div class="col-md-4 mb-1">
                <span class="text-muted">Период: </span>
                <span class="fw-bold">{{ planCard.dateFrom }} — {{ planCard.dateTo }}</span>
              </div>
              <div class="col-md-4 mb-1">
                <span class="text-muted">Счет: </span>
                <span class="fw-bold">{{ planCard.accountName }}</span>
              </div>
              <div class="col-md-4 mb-1">
                <span class="text-muted">Валюта: </span>
                <span class="fw-bold">{{ planCard.accountCurrency }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary cards -->
      <div class="col-xxl-4 col-md-4 d-flex">
        <div class="card info-card summary-card w-100">
          <div class="card-body">
            <div class="summary-title">План</div>
            <div class="summary-value">{{ planCard.amount }} {{ planCard.currencySymbol }}</div>
          </div>
        </div>
      </div>

      <div class="col-xxl-4 col-md-4 d-flex">
        <div class="card info-card summary-card w-100">
          <div class="card-body">
            <div class="summary-title">{{ planCard.type === 'EXPENSE' ? 'Потрачено' : 'Получено' }}</div>
            <div class="summary-value">{{ planCard.transactionAmount }} {{ planCard.currencySymbol }}</div>
          </div>
        </div>
      </div>

      <div class="col-xxl-4 col-md-4 d-flex">
        <div class="card info-card summary-card w-100">
          <div class="card-body">
            <div class="summary-title">{{ planCard.type === 'EXPENSE' ? 'Остаток' : 'До цели' }}</div>
            <div class="summary-value" >{{ planCard.remainingAmount < 0 ? 0 : planCard.remainingAmount }} {{ planCard.currencySymbol }}</div>
          </div>
        </div>
      </div>

      <!-- Progress -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Исполнение плана</h5>

            <div class="d-flex justify-content-between align-items-center mb-2">
              <div>
                <span>{{ planCard.statusLabel }}</span>
                <span v-if="planCard.isOverAmount" :class="{'fw-semibold': true, 'text-danger': plan.type === 'EXPENSE', 'text-success': plan.type === 'INCOME'}">
                    {{ planCard.overMessage }}
                  </span>
              </div>
              <div class="fw-bold">
                {{ planCard.transactionAmount }} / {{ planCard.amount }} {{ planCard.currencySymbol }}
                ({{ planCard.percent }}%)
              </div>
            </div>

            <ChartVerticalBar
                v-if="planCard.charData.length > 0"
                title=""
                height="200"
                planTitle="План"
                factTitle="Факт"
                :type="planCard.type"
                :currency="planCard.accountCurrency"
                :elements="planCard.charData"
            />
          </div>
        </div>
      </div>

      <!-- Categories chart -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Структура по категориям</h5>
            <div class="d-flex flex-wrap gap-2 mt-3" v-if="categories.length > 0">
              <draggable
                  v-model="planItemCards"
                  item-key="id"
                  tag="div"
                  class="d-flex flex-wrap gap-2"
                  @end="emit('dragItems', planItemCards);"
              >
                <template #item="{ element: item }">
                  <button
                      type="button"
                      class="btn btn-outline-info btn-lg"
                      @click="emit('updateItem', item)"
                  >
                    <span class="fw-semibold">{{ item.categoryName }}</span>
                    <br>
                    <span class="small text-muted">
                      {{ item.transactionsAmount }} / {{ item.amount }} {{ planCard.currencySymbol }}
                    </span>
                  </button>
                </template>
              </draggable>

              <button
                  type="button"
                  class="btn btn-outline-info btn-lg"
                  @click="emit('addItem')"
              >
                + Добавить
              </button>
            </div>
            <div v-else>
              Добавьте категории
            </div>
            <ChartVerticalBar
                v-if="planCard.planItems.length > 0"
                title="Разбивка по категориям"
                height="400"
                planTitle="План"
                factTitle="Факт"
                :type="planCard.type"
                :currency="planCard.accountCurrency"
                :elements="planCard.charElements"
            />
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import {formatLocal, getCurrencySymbol} from '@/api/helpers';
import {getProgressBar, getScopeTypeName, getChartVerticalBarElements} from "@/features/plans/composables/usePlan";
import ChartVerticalBar from "@/features/plans/components/ChartVerticalBar.vue";
import draggable from 'vuedraggable';
import ActionDropdown from "@/components/ui/components/ActionDropdown.vue";

const props = defineProps({
  plan: { type: Object, required: true },
  categories: { type: Array, required: false },
});
const emit = defineEmits(['update', 'delete', 'addItem', 'updateItem', 'dragItems']);

const planItemCards = ref([]);
const buildPlanItems = async (planItems) => {
  planItemCards.value = [...planItems];
};

const planCard = computed(() => {
  const plan = props.plan;
  const progress = getProgressBar(plan);
  let barClass = '';
  let statusLabel = '';
  const currencySymbol = getCurrencySymbol(plan.accountCurrency);
  const charElements = getChartVerticalBarElements(plan);

  if (plan.type === 'EXPENSE') {
    if (progress.percent > 75){
      barClass = 'bg-warning';
      statusLabel = '🟡 Близко к лимиту ';
    } else {
      barClass = 'bg-success';
      statusLabel = '🟢 В пределах ';
    }
    if (progress.percent >= 100) {
      barClass = 'progress-bar-striped bg-danger';
      statusLabel = '🔴 Перерасход ';
    }
  } else {
    if (progress.percent > 75){
      barClass = 'plan-bar-expense-medium';
      statusLabel = '🟡 Почти выполнен ';
    } else {
      barClass = 'plan-bar-expense-low';
      statusLabel = '🟡 В процессе ';
    }
    if (progress.percent >= 100) {
      barClass = 'progress-bar-striped plan-bar-expense-high';
      statusLabel = '🟢 План выполнен ';
    }
  }
  let overMessage = '';
  if (progress.isOverAmount) {
    overMessage = plan.type === 'EXPENSE'
        ? `- ${Math.abs(progress.remainingAmount)} ${currencySymbol}`
        : `+ ${Math.abs(progress.remainingAmount)} ${currencySymbol}`;
  }
  return {
    ...plan,
    ...progress,
    barClass: barClass,
    dateFrom: formatLocal(plan.dateFrom),
    dateTo: formatLocal(plan.dateTo),
    amount: progress.amount,
    overMessage: overMessage,
    scopeTypeLabel: getScopeTypeName(plan),
    statusLabel: statusLabel,
    currencySymbol: currencySymbol,
    charElements: charElements,
    charData: [
      {name: "Общее", plan: progress.amount, fact: progress.transactionAmount}
    ],
  };
});

watch(
    () => props.plan,
    (plan) => {
      buildPlanItems(plan?.planItems ?? [])
    },
    { immediate: true, deep: true }
)

</script>

<style>
.summary-card {
  min-height: 120px;
}

.summary-card .card-body {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1.25rem;
  text-align: center;
}

.summary-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #012970;
  margin-bottom: 0.35rem;
}

.summary-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  color: #000000;
}

.category-chip {
  background: #f6f9ff;
  border: 1px solid #dce3f1;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  min-width: 120px;
  transition: 0.2s;
}

.category-chip:hover {
  background: #eef3ff;
  border-color: #4154f1;
}

.category-chip.add {
  border-style: dashed;
  color: #4154f1;
}
</style>